// Professional EN/PL translations for the entire SubZero app.
// Polish is the default language. Diacritics are written natively.

export const TRANSLATIONS = {
  en: {
    // Common / nav
    "nav.dashboard": "Dashboard",
    "nav.scan": "Scan Receipt",
    "nav.receipts": "Receipts",
    "nav.subscriptions": "Subscriptions",
    "nav.settings": "Settings",
    "nav.admin": "Admin",
    "common.upgrade": "Upgrade",
    "common.freePlan": "Free plan",
    "common.free": "Free",
    "common.premium": "Premium",
    "common.signOut": "Sign out",
    "common.cancel": "Cancel",
    "common.save": "Save",
    "common.saving": "Saving...",
    "common.delete": "Delete",
    "common.update": "Update",
    "common.add": "Add",
    "common.back": "Back",
    "sidebar.freePlanDesc": "Unlock unlimited scans, subscriptions & CSV export.",

    // Login
    "login.back": "Back to home",
    "login.welcome": "Welcome back",
    "login.subtitle": "Sign in to manage your receipts and subscriptions.",
    "login.google": "Continue with Google",
    "login.agree": "By continuing you agree to our",
    "login.terms": "Terms",
    "login.and": "and",
    "login.privacy": "Privacy Policy",

    // Landing
    "landing.signIn": "Sign in",
    "landing.pricing": "Pricing",
    "landing.features": "Features",
    "landing.badge": "Powered by GPT-4o Vision",
    "landing.heroTitle1": "AI Receipt &",
    "landing.heroTitle2": "Subscription",
    "landing.heroTitle3": "Manager",
    "landing.heroSubtitle": "Scan receipts, auto-categorize spending, and never miss a renewal. SubZero turns financial clutter into clarity.",
    "landing.continueGoogle": "Continue with Google",
    "landing.viewPricing": "View Pricing",
    "landing.statPerScan": "Per scan",
    "landing.statCategories": "Categories",
    "landing.statLanguages": "Languages",
    "landing.thisMonth": "This month",
    "landing.featuresTitle": "Everything you need to stay on top of spending",
    "landing.featuresSubtitle": "A focused toolkit for receipts, subscriptions and renewals.",
    "landing.feat1Title": "AI Receipt Scanner",
    "landing.feat1Desc": "Snap a photo and GPT-4o Vision extracts merchant, amount, date & category in seconds.",
    "landing.feat2Title": "Subscription Tracker",
    "landing.feat2Desc": "Keep every recurring payment in one place with renewal cadence and totals.",
    "landing.feat3Title": "Renewal Alerts",
    "landing.feat3Desc": "Never get surprise-charged again. See what renews in the next 7 and 30 days.",
    "landing.feat4Title": "Spending Insights",
    "landing.feat4Desc": "Monthly trends and category breakdowns visualised on a clean dark dashboard.",
    "landing.feat5Title": "CSV Export",
    "landing.feat5Desc": "Export all your expenses to CSV for accounting and taxes (Premium).",
    "landing.feat6Title": "Private & Secure",
    "landing.feat6Desc": "Your data is isolated per account. Sign in securely with Google.",
    "landing.pricingTitle": "Simple pricing",
    "landing.pricingSubtitle": "Start free. Upgrade when you need more.",
    "landing.free": "Free",
    "landing.freeF1": "Up to 10 receipt scans",
    "landing.freeF2": "Track up to 5 subscriptions",
    "landing.freeF3": "Dashboard & renewal alerts",
    "landing.freeF4": "AI categorization",
    "landing.getStartedFree": "Get started free",
    "landing.bestValue": "Best value",
    "landing.premium": "Premium",
    "landing.premF1": "Unlimited receipt scans",
    "landing.premF2": "Unlimited subscriptions",
    "landing.premF3": "CSV export for accounting",
    "landing.premF4": "Priority renewal alerts",
    "landing.premF5": "Full spending analytics",
    "landing.upgradePremium": "Upgrade to Premium",
    "landing.faqTitle": "Frequently asked questions",
    "landing.faq1Q": "How does the AI receipt scanner work?",
    "landing.faq1A": "Upload or photograph a receipt and our GPT-4o Vision engine reads the merchant, total, date and category automatically. You can review and edit before saving.",
    "landing.faq2Q": "What's the difference between Free and Premium?",
    "landing.faq2A": "Free includes up to 10 receipt scans and 5 tracked subscriptions. Premium unlocks unlimited scans, unlimited subscriptions and CSV export.",
    "landing.faq3Q": "How do I upgrade to Premium?",
    "landing.faq3A": "Premium for the Google Play version is coming soon. On the free plan you can already manage your receipts and subscriptions.",
    "landing.faq4Q": "Is my financial data safe?",
    "landing.faq4A": "Yes. Every account's data is isolated, and you authenticate securely with your Google account. Read our Privacy Policy and AI Processing Notice for details.",
    "landing.demoMerchant1": "Whole Foods",
    "landing.demoMerchant2": "Netflix",
    "landing.demoMerchant3": "Uber",

    // Dashboard
    "dashboard.welcome": "Welcome back, {name}",
    "dashboard.subtitle": "Here's your spending overview.",
    "dashboard.scanReceipt": "Scan Receipt",
    "kpi.thisMonth": "This month",
    "kpi.receipts": "Receipts",
    "kpi.subscriptions": "Subscriptions",
    "kpi.renewals": "Renewals (30d)",
    "dashboard.monthlySpend": "Monthly spend",
    "dashboard.byCategory": "By category",
    "dashboard.upcomingRenewals": "Upcoming renewals",
    "dashboard.manage": "Manage",
    "dashboard.noExpensesMonth": "No expenses this month yet.",
    "dashboard.noRenewals": "No renewals in the next 30 days.",
    "dashboard.addSubscription": "Add a subscription",
    "dashboard.scanFirst": "Scan your first receipt to see spending trends.",
    "dashboard.scanA": "Scan a receipt",
    "chart.spend": "Spend",

    // Usage meter
    "usage.planUsage": "Plan Usage",
    "usage.receiptScans": "Receipt scans",
    "usage.subscriptions": "Subscriptions",
    "usage.unlimited": "Unlimited",
    "usage.upgradeUnlimited": "Upgrade for unlimited",

    // Renewal phrasing
    "renewal.overdue": "{n}d overdue",
    "renewal.today": "Today",
    "renewal.inDays": "in {n}d",
    "renewal.renewsToday": "Renews today",
    "renewal.renewsIn": "Renews in {n}d",
    "renewal.noDate": "No date",
    "renewal.next": "Next: {date}",

    // Scanner
    "scan.title": "Scan a receipt",
    "scan.subtitle": "Upload a photo and AI extracts the details automatically.",
    "scan.dropHere": "Drop a receipt here",
    "scan.orBrowse": "or click to browse · PNG, JPG, WEBP",
    "scan.reading": "Reading your receipt...",
    "scan.readingDesc": "GPT-4o Vision is extracting merchant, amount, date and category.",
    "scan.receiptImage": "Receipt image",
    "scan.confidence": "AI confidence {n}%",
    "scan.reviewSave": "Review & save",
    "scan.merchantPlaceholder": "Store name",
    "scan.optional": "Optional",
    "scan.saveReceipt": "Save receipt",
    "scan.analyzing": "Analyzing receipt...",
    "scan.chooserTitle": "How would you like to add the receipt?",
    "scan.chooserHelp": "Take a photo right away or pick an existing image.",
    "scan.optCamera": "Take a photo of the receipt",
    "scan.optCameraHint": "Opens the camera inside SubZero. The photo goes straight to scanning and is not saved to your gallery.",
    "scan.optCameraHintNative": "Opens your device camera. Whether the photo is kept in your gallery depends on your device.",
    "scan.cam.title": "Take a photo of the receipt",
    "scan.cam.hint": "Fit the receipt inside the frame and tap the shutter.",
    "scan.cam.starting": "Starting camera...",
    "scan.cam.shutter": "Take photo",
    "scan.cam.flip": "Switch camera",
    "scan.cam.useNative": "Use the device camera instead",
    "scan.cam.errPermission": "Camera access was denied. Allow camera access for SubZero or use the device camera.",
    "scan.cam.errNoCamera": "No camera was found on this device.",
    "scan.cam.errGeneric": "The in-app camera could not be started.",
    "scan.cam.notSaved": "Photos taken here are used for scanning only and are not saved to your gallery.",
    "scan.optGallery": "Choose a photo from the gallery",
    "scan.optGalleryHint": "Pick an existing photo from your phone.",
    "scan.optFile": "Choose a file",
    "scan.optFileHint": "PNG, JPG or WEBP from your files.",
    "scan.dragHint": "You can also drag & drop an image anywhere in this box.",
    "scan.cancel": "Cancel",
    "warn.title": "Please review before saving",
    "warn.amountHigh": "Warning: the detected amount looks unusually high. Please check it before saving.",
    "warn.merchantMissing": "The merchant name was not recognized. Please fill it in before saving.",
    "warn.dateMissing": "The purchase date was not recognized. Please check it before saving.",
    "warn.merchantAndDateMissing": "Merchant name and purchase date were not recognized.",
    "warn.dateOld": "This receipt has an old date \u2014 it will be stored but won't count toward this month's total.",
    "warn.dateConfirm": "Please confirm the date shown on the receipt \u2014 the scanner wasn't fully sure it read it correctly.",
    "warn.duplicate": "This receipt may already be in your account. Please check before saving it again.",
    "verify.title": "Document check",
    "verify.green": "No obvious inconsistencies detected",
    "verify.yellow": "Needs review",
    "verify.red": "Some data may require correction \u2014 review before saving",
    "verify.disclaimer": "Beta \u2014 experimental feature. The result is an indicator and does not confirm document authenticity.",
    "verify.check.merchant": "Merchant extracted",
    "verify.check.amount": "Total amount extracted",
    "verify.check.date": "Date extracted",
    "verify.check.future": "Date is not in the future",
    "verify.check.old": "Date is within an acceptable range",
    "verify.check.oldWarn": "Please check the document date",
    "verify.check.confidence": "Date read with confidence",
    "verify.check.consistency": "Receipt fields are internally consistent",
    "warn.dateFuture": "The purchase date is in the future. Please check it before saving.",

    // Shared field labels
    "field.merchant": "Merchant",
    "field.amount": "Amount",
    "field.currency": "Currency",
    "field.date": "Date",
    "field.category": "Category",
    "field.notes": "Notes",
    "field.name": "Name",
    "field.billingCycle": "Billing cycle",
    "field.nextRenewal": "Next renewal date",

    // Receipts
    "receipts.title": "Receipts",
    "receipts.summary": "{count} receipts · {total} shown",
    "receipts.exportCsv": "Export CSV",
    "receipts.scan": "Scan",
    "receipts.searchPlaceholder": "Search merchant...",
    "receipts.allCategories": "All categories",
    "receipts.noneTitle": "No receipts yet",
    "receipts.noneDesc": "Scan your first receipt to get started.",
    "receipts.noMatch": "No matches",
    "receipts.noMatchDesc": "Try a different search or filter.",
    "receipts.scanAreceipt": "Scan a receipt",
    "receipts.colMerchant": "Merchant",
    "receipts.colCategory": "Category",
    "receipts.colDate": "Date",
    "receipts.colAmount": "Amount",
    "receipts.oldNote": "Old receipts are stored here, but only current-month receipts count toward \u201cThis month\u201d.",
    "receipts.notThisMonth": "not this month",
    "receipts.deleteTitle": "Delete receipt?",
    "receipts.deleteDesc": "This will permanently remove \"{merchant}\" ({amount}).",

    // Subscriptions
    "subs.title": "Subscriptions",
    "subs.summary": "{count} active · ~{total}/mo recurring",
    "subs.add": "Add subscription",
    "subs.noneTitle": "No subscriptions yet",
    "subs.noneDesc": "Add your recurring payments to track renewals.",
    "subs.namePlaceholder": "e.g. Netflix",
    "subs.editTitle": "Edit subscription",
    "subs.addTitle": "Add subscription",
    "subs.deleteTitle": "Delete subscription?",
    "subs.deleteDesc": "Remove \"{name}\" from tracking?",

    // Settings
    "settings.title": "Settings",
    "settings.account": "Account",
    "settings.goPremium": "Go Premium",
    "settings.goPremiumDesc": "Unlimited scans & subscriptions, plus CSV export.",
    "settings.upgradeNow": "Upgrade now",
    "settings.legal": "Legal",
    "settings.privacy": "Privacy Policy",
    "settings.terms": "Terms of Service",
    "settings.aiNotice": "AI Processing Notice",
    "settings.deleteAccount": "Delete account / Request data deletion",
    "settings.language": "Language",

    // Admin
    "admin.title": "Admin panel",
    "admin.subtitle": "Manually activate Premium for users.",
    "admin.activateByEmail": "Activate Premium by email",
    "admin.emailPlaceholder": "user@example.com",
    "admin.activatePremium": "Activate Premium",
    "admin.activating": "Activating...",
    "admin.mustSignIn": "The user must have signed in at least once before activation.",
    "admin.searchUsers": "Search users...",
    "admin.joined": "joined",
    "admin.noUsers": "No users found.",
    "admin.adminTag": "Admin",

    // Upgrade modal
    "upgrade.title": "Upgrade to Premium",
    "upgrade.premium": "Premium",
    "upgrade.oneTime": "\u2014 soon on Google Play",
    "upgrade.f1": "Unlimited receipt scans",
    "upgrade.f2": "Unlimited subscription tracking",
    "upgrade.f3": "CSV export of all expenses",
    "upgrade.f4": "Priority renewal alerts",
    "upgrade.f5": "Full spending analytics",
    "upgrade.continue": "Continue to Payment",
    "upgrade.afterPayment": "After payment, Premium is activated for your account by our team. You'll be notified by email.",
    "upgrade.appComingSoon": "Premium in the Google Play version will be available soon.",
    "upgrade.appComingSoonHint": "You can already manage receipts and subscriptions on the free plan.",

    // Google Play Billing (inside the Android app only)
    "billing.monthly": "Monthly",
    "billing.yearly": "Yearly",
    "billing.perMonth": "billed monthly, cancel anytime",
    "billing.perYear": "billed yearly, cancel anytime",
    "billing.buy": "Subscribe",
    "billing.restore": "Restore purchases",
    "billing.playNotice": "Payment is handled securely by Google Play. Premium unlocks only after the purchase is verified by SubZero.",
    "billing.success": "Premium activated. Thank you!",
    "billing.restored": "Your Premium subscription has been restored.",
    "billing.cancelled": "Purchase cancelled.",
    "billing.alreadyPremium": "You already have Premium on this account.",
    "billing.catalogError": "Could not load prices from Google Play.",
    "billing.retry": "Try again",
    "billing.err.unavailable": "Google Play Billing is not available here. Use the SubZero app from Google Play.",
    "billing.err.purchaseFailed": "The purchase could not be started. Please try again.",
    "billing.err.verifyFailed": "We could not verify the purchase. Premium was not activated. Try \"Restore purchases\" in a moment.",
    "billing.err.verifyUnavailable": "Purchase verification is temporarily unavailable. Your purchase is safe - try \"Restore purchases\" later.",
    "billing.err.verifyRejected": "Google Play did not confirm this purchase for your account.",
    "billing.err.tokenOtherAccount": "This purchase is already linked to a different SubZero account.",
    "billing.err.notActive": "This subscription is not active.",
    "billing.err.googleUnavailable": "Google Play is temporarily unavailable. Please try again.",
    "billing.err.notSignedIn": "Please sign in again to continue.",
    "billing.err.nothingToRestore": "No purchases found for this Google account.",
    "billing.err.nothingActive": "No active Premium subscription was found.",

    // Toasts
    "toast.scanned": "Receipt scanned! Review the details below.",
    "toast.scanFailed": "Scan failed. Please try again.",
    "toast.imageType": "Please upload a PNG, JPG or WEBP image.",
    "toast.merchantRequired": "Merchant is required.",
    "toast.receiptSaved": "Receipt saved.",
    "toast.receiptSaveError": "Could not save receipt.",
    "toast.receiptDeleted": "Receipt deleted.",
    "toast.deleteError": "Could not delete.",
    "toast.csvPremium": "CSV export is a Premium feature.",
    "toast.csvExported": "Exported CSV.",
    "toast.exportFailed": "Export failed.",
    "toast.nameRequired": "Name is required.",
    "toast.subUpdated": "Subscription updated.",
    "toast.subAdded": "Subscription added.",
    "toast.subDeleted": "Deleted.",
    "toast.saveError": "Could not save.",
    "toast.enterEmail": "Enter an email.",
    "toast.premiumActivated": "Premium activated for {email}",
    "toast.activateError": "Could not activate.",
    "toast.updateFailed": "Update failed.",
    "toast.usersLoadError": "Could not load users.",

    // Auth callback / legal chrome
    "auth.signingIn": "Signing you in...",
    "legal.deleteAccountTab": "Delete account",

    // Frontend-side localization of backend error messages (text mirrors backend wording)
    "err.freeLimitScans": "Free plan limit reached ({n} receipts). Upgrade to Premium for unlimited scans.",
    "err.freeLimitSubs": "Free plan limit reached ({n} subscriptions). Upgrade to Premium for unlimited tracking.",
    "err.emptyFile": "Empty file",
    "err.imageTooLarge": "Image too large (max {n}MB)",
    "err.couldNotRead": "Could not read receipt",
    "err.receiptNotFound": "Receipt not found",
    "err.subNotFound": "Subscription not found",
  },

  pl: {
    // Common / nav
    "nav.dashboard": "Panel g\u0142\u00f3wny",
    "nav.scan": "Skanuj paragon",
    "nav.receipts": "Paragony",
    "nav.subscriptions": "Subskrypcje",
    "nav.settings": "Ustawienia",
    "nav.admin": "Administrator",
    "common.upgrade": "Przejd\u017a na Premium",
    "common.freePlan": "Plan darmowy",
    "common.free": "Darmowy",
    "common.premium": "Premium",
    "common.signOut": "Wyloguj si\u0119",
    "common.cancel": "Anuluj",
    "common.save": "Zapisz",
    "common.saving": "Zapisywanie...",
    "common.delete": "Usu\u0144",
    "common.update": "Zaktualizuj",
    "common.add": "Dodaj",
    "common.back": "Wstecz",
    "sidebar.freePlanDesc": "Odblokuj nielimitowane skany, subskrypcje i eksport CSV.",

    // Login
    "login.back": "Powr\u00f3t do strony g\u0142\u00f3wnej",
    "login.welcome": "Witaj ponownie",
    "login.subtitle": "Zaloguj si\u0119, aby zarz\u0105dza\u0107 paragonami i subskrypcjami.",
    "login.google": "Kontynuuj z Google",
    "login.agree": "Kontynuuj\u0105c, akceptujesz nasz",
    "login.terms": "Regulamin",
    "login.and": "oraz",
    "login.privacy": "Polityk\u0119 Prywatno\u015bci",

    // Landing
    "landing.signIn": "Zaloguj si\u0119",
    "landing.pricing": "Cennik",
    "landing.features": "Funkcje",
    "landing.badge": "Nap\u0119dzane przez GPT-4o Vision",
    "landing.heroTitle1": "Asystent AI do",
    "landing.heroTitle2": "paragon\u00f3w",
    "landing.heroTitle3": "i subskrypcji",
    "landing.heroSubtitle": "Skanuj paragony, automatycznie kategoryzuj wydatki i nie przegap \u017cadnego odnowienia. SubZero zamienia finansowy chaos w przejrzysto\u015b\u0107.",
    "landing.continueGoogle": "Kontynuuj z Google",
    "landing.viewPricing": "Zobacz cennik",
    "landing.statPerScan": "Na skan",
    "landing.statCategories": "Kategorii",
    "landing.statLanguages": "J\u0119zyki",
    "landing.thisMonth": "W tym miesi\u0105cu",
    "landing.featuresTitle": "Wszystko, czego potrzebujesz, by panowa\u0107 nad wydatkami",
    "landing.featuresSubtitle": "Skoncentrowany zestaw narz\u0119dzi do paragon\u00f3w, subskrypcji i odnowie\u0144.",
    "landing.feat1Title": "Skaner paragon\u00f3w AI",
    "landing.feat1Desc": "Zr\u00f3b zdj\u0119cie, a GPT-4o Vision w kilka sekund odczyta sprzedawc\u0119, kwot\u0119, dat\u0119 i kategori\u0119.",
    "landing.feat2Title": "\u015aledzenie subskrypcji",
    "landing.feat2Desc": "Trzymaj wszystkie cykliczne p\u0142atno\u015bci w jednym miejscu wraz z cz\u0119stotliwo\u015bci\u0105 i sumami.",
    "landing.feat3Title": "Alerty odnowie\u0144",
    "landing.feat3Desc": "Koniec z niespodziewanymi op\u0142atami. Sprawd\u017a, co odnawia si\u0119 w ci\u0105gu 7 i 30 dni.",
    "landing.feat4Title": "Analiza wydatk\u00f3w",
    "landing.feat4Desc": "Trendy miesi\u0119czne i podzia\u0142 na kategorie na czytelnym, ciemnym pulpicie.",
    "landing.feat5Title": "Eksport CSV",
    "landing.feat5Desc": "Eksportuj wszystkie wydatki do CSV na potrzeby ksi\u0119gowo\u015bci i podatk\u00f3w (Premium).",
    "landing.feat6Title": "Prywatno\u015b\u0107 i bezpiecze\u0144stwo",
    "landing.feat6Desc": "Twoje dane s\u0105 odseparowane dla ka\u017cdego konta. Logujesz si\u0119 bezpiecznie przez Google.",
    "landing.pricingTitle": "Prosty cennik",
    "landing.pricingSubtitle": "Zacznij za darmo. Przejd\u017a na wy\u017cszy plan, gdy potrzebujesz wi\u0119cej.",
    "landing.free": "Darmowy",
    "landing.freeF1": "Do 10 skan\u00f3w paragon\u00f3w",
    "landing.freeF2": "\u015aled\u017a do 5 subskrypcji",
    "landing.freeF3": "Pulpit i alerty odnowie\u0144",
    "landing.freeF4": "Kategoryzacja AI",
    "landing.getStartedFree": "Zacznij za darmo",
    "landing.bestValue": "Najlepsza warto\u015b\u0107",
    "landing.premium": "Premium",
    "landing.premF1": "Nielimitowane skany paragon\u00f3w",
    "landing.premF2": "Nielimitowane subskrypcje",
    "landing.premF3": "Eksport CSV dla ksi\u0119gowo\u015bci",
    "landing.premF4": "Priorytetowe alerty odnowie\u0144",
    "landing.premF5": "Pe\u0142na analityka wydatk\u00f3w",
    "landing.upgradePremium": "Przejd\u017a na Premium",
    "landing.faqTitle": "Najcz\u0119\u015bciej zadawane pytania",
    "landing.faq1Q": "Jak dzia\u0142a skaner paragon\u00f3w AI?",
    "landing.faq1A": "Prze\u015blij lub sfotografuj paragon, a nasz silnik GPT-4o Vision automatycznie odczyta sprzedawc\u0119, sum\u0119, dat\u0119 i kategori\u0119. Mo\u017cesz wszystko sprawdzi\u0107 i poprawi\u0107 przed zapisem.",
    "landing.faq2Q": "Jaka jest r\u00f3\u017cnica mi\u0119dzy planem Darmowym a Premium?",
    "landing.faq2A": "Plan Darmowy obejmuje do 10 skan\u00f3w paragon\u00f3w i 5 \u015bledzonych subskrypcji. Premium odblokowuje nielimitowane skany, nielimitowane subskrypcje i eksport CSV.",
    "landing.faq3Q": "Jak przej\u015b\u0107 na Premium?",
    "landing.faq3A": "Premium w wersji Google Play b\u0119dzie dost\u0119pne wkr\u00f3tce. W planie darmowym mo\u017cesz ju\u017c zarz\u0105dza\u0107 paragonami i subskrypcjami.",
    "landing.faq4Q": "Czy moje dane finansowe s\u0105 bezpieczne?",
    "landing.faq4A": "Tak. Dane ka\u017cdego konta s\u0105 odseparowane, a logowanie odbywa si\u0119 bezpiecznie przez konto Google. Szczeg\u00f3\u0142y znajdziesz w Polityce Prywatno\u015bci i Informacji o Przetwarzaniu przez AI.",
    "landing.demoMerchant1": "Biedronka",
    "landing.demoMerchant2": "Netflix",
    "landing.demoMerchant3": "Uber",

    // Dashboard
    "dashboard.welcome": "Witaj ponownie, {name}",
    "dashboard.subtitle": "Oto przegl\u0105d Twoich wydatk\u00f3w.",
    "dashboard.scanReceipt": "Skanuj paragon",
    "kpi.thisMonth": "W tym miesi\u0105cu",
    "kpi.receipts": "Paragony",
    "kpi.subscriptions": "Subskrypcje",
    "kpi.renewals": "Odnowienia (30 dni)",
    "dashboard.monthlySpend": "Wydatki miesi\u0119czne",
    "dashboard.byCategory": "Wed\u0142ug kategorii",
    "dashboard.upcomingRenewals": "Nadchodz\u0105ce odnowienia",
    "dashboard.manage": "Zarz\u0105dzaj",
    "dashboard.noExpensesMonth": "Brak wydatk\u00f3w w tym miesi\u0105cu.",
    "dashboard.noRenewals": "Brak odnowie\u0144 w ci\u0105gu najbli\u017cszych 30 dni.",
    "dashboard.addSubscription": "Dodaj subskrypcj\u0119",
    "dashboard.scanFirst": "Zeskanuj pierwszy paragon, aby zobaczy\u0107 trendy wydatk\u00f3w.",
    "dashboard.scanA": "Zeskanuj paragon",
    "chart.spend": "Wydatki",

    // Usage meter
    "usage.planUsage": "Wykorzystanie planu",
    "usage.receiptScans": "Skany paragon\u00f3w",
    "usage.subscriptions": "Subskrypcje",
    "usage.unlimited": "Bez limitu",
    "usage.upgradeUnlimited": "Przejd\u017a na Premium bez limit\u00f3w",

    // Renewal phrasing
    "renewal.overdue": "zaleg\u0142e {n} dni",
    "renewal.today": "Dzi\u015b",
    "renewal.inDays": "za {n} dni",
    "renewal.renewsToday": "Odnawia si\u0119 dzi\u015b",
    "renewal.renewsIn": "Odnawia si\u0119 za {n} dni",
    "renewal.noDate": "Brak daty",
    "renewal.next": "Nast\u0119pne: {date}",

    // Scanner
    "scan.title": "Skanowanie paragonu",
    "scan.subtitle": "Prze\u015blij zdj\u0119cie, a AI automatycznie odczyta dane.",
    "scan.dropHere": "Upu\u015b\u0107 paragon tutaj",
    "scan.orBrowse": "lub kliknij, aby wybra\u0107 · PNG, JPG, WEBP",
    "scan.reading": "Odczytywanie paragonu...",
    "scan.readingDesc": "GPT-4o Vision odczytuje sprzedawc\u0119, kwot\u0119, dat\u0119 i kategori\u0119.",
    "scan.receiptImage": "Obraz paragonu",
    "scan.confidence": "Pewno\u015b\u0107 AI {n}%",
    "scan.reviewSave": "Sprawd\u017a i zapisz",
    "scan.merchantPlaceholder": "Nazwa sklepu",
    "scan.optional": "Opcjonalnie",
    "scan.saveReceipt": "Zapisz paragon",
    "scan.analyzing": "Analizuj\u0119 paragon...",
    "scan.chooserTitle": "Jak chcesz doda\u0107 paragon?",
    "scan.chooserHelp": "Mo\u017cesz zrobi\u0107 zdj\u0119cie od razu albo wybra\u0107 istniej\u0105cy obraz.",
    "scan.optCamera": "Zr\u00f3b zdj\u0119cie paragonu",
    "scan.optCameraHint": "Otwiera aparat w aplikacji SubZero. Zdj\u0119cie trafia od razu do skanowania i nie jest zapisywane w galerii.",
    "scan.optCameraHintNative": "Otwiera aparat urz\u0105dzenia. To, czy zdj\u0119cie zostanie w galerii, zale\u017cy od urz\u0105dzenia.",
    "scan.cam.title": "Zr\u00f3b zdj\u0119cie paragonu",
    "scan.cam.hint": "Ustaw paragon w ramce i naci\u015bnij migawk\u0119.",
    "scan.cam.starting": "Uruchamianie aparatu...",
    "scan.cam.shutter": "Zr\u00f3b zdj\u0119cie",
    "scan.cam.flip": "Prze\u0142\u0105cz aparat",
    "scan.cam.useNative": "U\u017cyj aparatu urz\u0105dzenia",
    "scan.cam.errPermission": "Odm\u00f3wiono dost\u0119pu do aparatu. Zezw\u00f3l SubZero na dost\u0119p do aparatu albo u\u017cyj aparatu urz\u0105dzenia.",
    "scan.cam.errNoCamera": "Nie znaleziono aparatu w tym urz\u0105dzeniu.",
    "scan.cam.errGeneric": "Nie uda\u0142o si\u0119 uruchomi\u0107 aparatu w aplikacji.",
    "scan.cam.notSaved": "Zdj\u0119cia zrobione tutaj s\u0142u\u017c\u0105 tylko do skanowania i nie s\u0105 zapisywane w galerii.",
    "scan.optGallery": "Wybierz zdj\u0119cie z galerii",
    "scan.optGalleryHint": "Wybierz istniej\u0105ce zdj\u0119cie z telefonu.",
    "scan.optFile": "Wybierz plik",
    "scan.optFileHint": "PNG, JPG lub WEBP z Twoich plik\u00f3w.",
    "scan.dragHint": "Mo\u017cesz te\u017c przeci\u0105gn\u0105\u0107 obraz w dowolne miejsce tej ramki.",
    "scan.cancel": "Anuluj",
    "warn.title": "Sprawd\u017a przed zapisaniem",
    "warn.amountHigh": "Uwaga: wykryta kwota wygl\u0105da nietypowo wysoko. Sprawd\u017a j\u0105 przed zapisaniem.",
    "warn.merchantMissing": "Nie rozpoznano nazwy sprzedawcy. Uzupe\u0142nij j\u0105 przed zapisaniem.",
    "warn.dateMissing": "Nie rozpoznano daty zakupu. Sprawd\u017a j\u0105 przed zapisaniem.",
    "warn.merchantAndDateMissing": "Nie rozpoznano nazwy sprzedawcy ani daty zakupu.",
    "warn.dateOld": "Ten paragon ma star\u0105 dat\u0119 \u2014 zostanie zapisany, ale nie zwi\u0119kszy sumy w bie\u017c\u0105cym miesi\u0105cu.",
    "warn.dateConfirm": "Potwierd\u017a dat\u0119 widoczn\u0105 na paragonie \u2014 skaner nie by\u0142 pewien, czy odczyta\u0142 j\u0105 poprawnie.",
    "warn.duplicate": "Ten paragon mo\u017ce ju\u017c znajdowa\u0107 si\u0119 na Twoim koncie. Sprawd\u017a przed ponownym zapisaniem.",
    "verify.title": "Weryfikacja dokumentu",
    "verify.green": "Brak oczywistych niesp\u00f3jno\u015bci",
    "verify.yellow": "Wymaga sprawdzenia",
    "verify.red": "Wykryto dane wymagaj\u0105ce korekty \u2014 sprawd\u017a przed zapisem",
    "verify.disclaimer": "Beta \u2014 funkcja eksperymentalna. Wynik jest wskaz\u00f3wk\u0105 i nie stanowi potwierdzenia autentyczno\u015bci dokumentu.",
    "verify.check.merchant": "Wykryto sprzedawc\u0119",
    "verify.check.amount": "Wykryto kwot\u0119 ca\u0142kowit\u0105",
    "verify.check.date": "Wykryto dat\u0119",
    "verify.check.future": "Data nie jest z przysz\u0142o\u015bci",
    "verify.check.old": "Data jest w akceptowalnym zakresie",
    "verify.check.oldWarn": "Sprawd\u017a dat\u0119 dokumentu",
    "verify.check.confidence": "Data odczytana pewnie",
    "verify.check.consistency": "Pola paragonu s\u0105 wewn\u0119trznie sp\u00f3jne",
    "warn.dateFuture": "Data zakupu jest w przysz\u0142o\u015bci. Sprawd\u017a j\u0105 przed zapisaniem.",

    // Shared field labels
    "field.merchant": "Sprzedawca",
    "field.amount": "Kwota",
    "field.currency": "Waluta",
    "field.date": "Data",
    "field.category": "Kategoria",
    "field.notes": "Notatki",
    "field.name": "Nazwa",
    "field.billingCycle": "Cykl rozliczeniowy",
    "field.nextRenewal": "Data nast\u0119pnego odnowienia",

    // Receipts
    "receipts.title": "Paragony",
    "receipts.summary": "{count} paragon\u00f3w · pokazano {total}",
    "receipts.exportCsv": "Eksport CSV",
    "receipts.scan": "Skanuj",
    "receipts.searchPlaceholder": "Szukaj sprzedawcy...",
    "receipts.allCategories": "Wszystkie kategorie",
    "receipts.noneTitle": "Brak paragon\u00f3w",
    "receipts.noneDesc": "Zeskanuj pierwszy paragon, aby rozpocz\u0105\u0107.",
    "receipts.noMatch": "Brak wynik\u00f3w",
    "receipts.noMatchDesc": "Spr\u00f3buj innego wyszukiwania lub filtra.",
    "receipts.scanAreceipt": "Zeskanuj paragon",
    "receipts.colMerchant": "Sprzedawca",
    "receipts.colCategory": "Kategoria",
    "receipts.colDate": "Data",
    "receipts.colAmount": "Kwota",
    "receipts.oldNote": "Stare paragony s\u0105 tutaj przechowywane, ale do \u201eW tym miesi\u0105cu\u201d licz\u0105 si\u0119 tylko paragony z bie\u017c\u0105cego miesi\u0105ca.",
    "receipts.notThisMonth": "poza bie\u017c\u0105cym miesi\u0105cem",
    "receipts.deleteTitle": "Usun\u0105\u0107 paragon?",
    "receipts.deleteDesc": "Spowoduje to trwa\u0142e usuni\u0119cie \"{merchant}\" ({amount}).",

    // Subscriptions
    "subs.title": "Subskrypcje",
    "subs.summary": "{count} aktywnych · ~{total}/mies. cyklicznie",
    "subs.add": "Dodaj subskrypcj\u0119",
    "subs.noneTitle": "Brak subskrypcji",
    "subs.noneDesc": "Dodaj swoje cykliczne p\u0142atno\u015bci, aby \u015bledzi\u0107 odnowienia.",
    "subs.namePlaceholder": "np. Netflix",
    "subs.editTitle": "Edytuj subskrypcj\u0119",
    "subs.addTitle": "Dodaj subskrypcj\u0119",
    "subs.deleteTitle": "Usun\u0105\u0107 subskrypcj\u0119?",
    "subs.deleteDesc": "Usun\u0105\u0107 \"{name}\" ze \u015bledzenia?",

    // Settings
    "settings.title": "Ustawienia",
    "settings.account": "Konto",
    "settings.goPremium": "Przejd\u017a na Premium",
    "settings.goPremiumDesc": "Nielimitowane skany i subskrypcje oraz eksport CSV.",
    "settings.upgradeNow": "Przejd\u017a teraz",
    "settings.legal": "Dokumenty prawne",
    "settings.privacy": "Polityka Prywatno\u015bci",
    "settings.terms": "Regulamin",
    "settings.aiNotice": "Informacja o Przetwarzaniu przez AI",
    "settings.deleteAccount": "Usu\u0144 konto / Pro\u015bba o usuni\u0119cie danych",
    "settings.language": "J\u0119zyk",

    // Admin
    "admin.title": "Panel administratora",
    "admin.subtitle": "R\u0119czna aktywacja Premium dla u\u017cytkownik\u00f3w.",
    "admin.activateByEmail": "Aktywuj Premium na podstawie e-maila",
    "admin.emailPlaceholder": "uzytkownik@example.com",
    "admin.activatePremium": "Aktywuj Premium",
    "admin.activating": "Aktywowanie...",
    "admin.mustSignIn": "U\u017cytkownik musi zalogowa\u0107 si\u0119 przynajmniej raz przed aktywacj\u0105.",
    "admin.searchUsers": "Szukaj u\u017cytkownik\u00f3w...",
    "admin.joined": "do\u0142\u0105czy\u0142",
    "admin.noUsers": "Nie znaleziono u\u017cytkownik\u00f3w.",
    "admin.adminTag": "Administrator",

    // Upgrade modal
    "upgrade.title": "Przejd\u017a na Premium",
    "upgrade.premium": "Premium",
    "upgrade.oneTime": "\u2014 wkr\u00f3tce w Google Play",
    "upgrade.f1": "Nielimitowane skany paragon\u00f3w",
    "upgrade.f2": "Nielimitowane \u015bledzenie subskrypcji",
    "upgrade.f3": "Eksport CSV wszystkich wydatk\u00f3w",
    "upgrade.f4": "Priorytetowe alerty odnowie\u0144",
    "upgrade.f5": "Pe\u0142na analityka wydatk\u00f3w",
    "upgrade.continue": "Przejd\u017a do p\u0142atno\u015bci",
    "upgrade.afterPayment": "Po dokonaniu p\u0142atno\u015bci nasz zesp\u00f3\u0142 aktywuje Premium dla Twojego konta. Powiadomimy Ci\u0119 e-mailem.",
    "upgrade.appComingSoon": "Premium w wersji Google Play b\u0119dzie dost\u0119pne wkr\u00f3tce.",
    "upgrade.appComingSoonHint": "W planie darmowym mo\u017cesz ju\u017c zarz\u0105dza\u0107 paragonami i subskrypcjami.",

    // Google Play Billing (tylko w aplikacji Android)
    "billing.monthly": "Miesi\u0119cznie",
    "billing.yearly": "Rocznie",
    "billing.perMonth": "p\u0142atno\u015b\u0107 co miesi\u0105c, anuluj w dowolnym momencie",
    "billing.perYear": "p\u0142atno\u015b\u0107 co rok, anuluj w dowolnym momencie",
    "billing.buy": "Subskrybuj",
    "billing.restore": "Przywr\u00f3\u0107 zakupy",
    "billing.playNotice": "P\u0142atno\u015b\u0107 obs\u0142uguje bezpiecznie Google Play. Premium odblokuje si\u0119 dopiero po weryfikacji zakupu przez SubZero.",
    "billing.success": "Premium aktywowane. Dzi\u0119kujemy!",
    "billing.restored": "Twoja subskrypcja Premium zosta\u0142a przywr\u00f3cona.",
    "billing.cancelled": "Zakup anulowany.",
    "billing.alreadyPremium": "To konto ma ju\u017c Premium.",
    "billing.catalogError": "Nie uda\u0142o si\u0119 pobra\u0107 cen z Google Play.",
    "billing.retry": "Spr\u00f3buj ponownie",
    "billing.err.unavailable": "Google Play Billing nie jest tu dost\u0119pny. U\u017cyj aplikacji SubZero z Google Play.",
    "billing.err.purchaseFailed": "Nie uda\u0142o si\u0119 rozpocz\u0105\u0107 zakupu. Spr\u00f3buj ponownie.",
    "billing.err.verifyFailed": "Nie uda\u0142o si\u0119 zweryfikowa\u0107 zakupu. Premium nie zosta\u0142o aktywowane. Za chwil\u0119 u\u017cyj opcji \"Przywr\u00f3\u0107 zakupy\".",
    "billing.err.verifyUnavailable": "Weryfikacja zakupu jest chwilowo niedost\u0119pna. Tw\u00f3j zakup jest bezpieczny - u\u017cyj p\u00f3\u017aniej opcji \"Przywr\u00f3\u0107 zakupy\".",
    "billing.err.verifyRejected": "Google Play nie potwierdzi\u0142 tego zakupu dla Twojego konta.",
    "billing.err.tokenOtherAccount": "Ten zakup jest ju\u017c powi\u0105zany z innym kontem SubZero.",
    "billing.err.notActive": "Ta subskrypcja nie jest aktywna.",
    "billing.err.googleUnavailable": "Google Play jest chwilowo niedost\u0119pny. Spr\u00f3buj ponownie.",
    "billing.err.notSignedIn": "Zaloguj si\u0119 ponownie, aby kontynuowa\u0107.",
    "billing.err.nothingToRestore": "Nie znaleziono zakup\u00f3w dla tego konta Google.",
    "billing.err.nothingActive": "Nie znaleziono aktywnej subskrypcji Premium.",

    // Toasts
    "toast.scanned": "Paragon zeskanowany! Sprawd\u017a szczeg\u00f3\u0142y poni\u017cej.",
    "toast.scanFailed": "Skanowanie nie powiod\u0142o si\u0119. Spr\u00f3buj ponownie.",
    "toast.imageType": "Prze\u015blij obraz w formacie PNG, JPG lub WEBP.",
    "toast.merchantRequired": "Sprzedawca jest wymagany.",
    "toast.receiptSaved": "Paragon zapisany.",
    "toast.receiptSaveError": "Nie uda\u0142o si\u0119 zapisa\u0107 paragonu.",
    "toast.receiptDeleted": "Paragon usuni\u0119ty.",
    "toast.deleteError": "Nie uda\u0142o si\u0119 usun\u0105\u0107.",
    "toast.csvPremium": "Eksport CSV to funkcja Premium.",
    "toast.csvExported": "Wyeksportowano CSV.",
    "toast.exportFailed": "Eksport nie powi\u00f3d\u0142 si\u0119.",
    "toast.nameRequired": "Nazwa jest wymagana.",
    "toast.subUpdated": "Subskrypcja zaktualizowana.",
    "toast.subAdded": "Subskrypcja dodana.",
    "toast.subDeleted": "Usuni\u0119to.",
    "toast.saveError": "Nie uda\u0142o si\u0119 zapisa\u0107.",
    "toast.enterEmail": "Wpisz adres e-mail.",
    "toast.premiumActivated": "Premium aktywowane dla {email}",
    "toast.activateError": "Nie uda\u0142o si\u0119 aktywowa\u0107.",
    "toast.updateFailed": "Aktualizacja nie powiod\u0142a si\u0119.",
    "toast.usersLoadError": "Nie uda\u0142o si\u0119 wczyta\u0107 u\u017cytkownik\u00f3w.",

    // Auth callback / legal chrome
    "auth.signingIn": "Logowanie...",
    "legal.deleteAccountTab": "Usu\u0144 konto",

    // Frontend-side localization of backend error messages
    "err.freeLimitScans": "Osi\u0105gni\u0119to limit planu darmowego ({n} paragon\u00f3w). Przejd\u017a na Premium, aby skanowa\u0107 bez limitu.",
    "err.freeLimitSubs": "Osi\u0105gni\u0119to limit planu darmowego ({n} subskrypcji). Przejd\u017a na Premium, aby \u015bledzi\u0107 bez limitu.",
    "err.emptyFile": "Pusty plik",
    "err.imageTooLarge": "Obraz jest za du\u017cy (maks. {n} MB)",
    "err.couldNotRead": "Nie uda\u0142o si\u0119 odczyta\u0107 paragonu",
    "err.receiptNotFound": "Nie znaleziono paragonu",
    "err.subNotFound": "Nie znaleziono subskrypcji",
  },

  vi: {
    // Common / nav
    "nav.dashboard": "Tổng quan",
    "nav.scan": "Quét hóa đơn",
    "nav.receipts": "Hóa đơn",
    "nav.subscriptions": "Gói đăng ký",
    "nav.settings": "Cài đặt",
    "nav.admin": "Quản trị",
    "common.upgrade": "Nâng cấp",
    "common.freePlan": "Gói miễn phí",
    "common.free": "Miễn phí",
    "common.premium": "Premium",
    "common.signOut": "Đăng xuất",
    "common.cancel": "Hủy",
    "common.save": "Lưu",
    "common.saving": "Đang lưu...",
    "common.delete": "Xóa",
    "common.update": "Cập nhật",
    "common.add": "Thêm",
    "common.back": "Quay lại",
    "sidebar.freePlanDesc": "Mở khóa quét không giới hạn, gói đăng ký không giới hạn và xuất CSV.",

    // Login
    "login.back": "Về trang chủ",
    "login.welcome": "Chào mừng trở lại",
    "login.subtitle": "Đăng nhập để quản lý hóa đơn và các gói đăng ký của bạn.",
    "login.google": "Tiếp tục với Google",
    "login.agree": "Khi tiếp tục, bạn đồng ý với",
    "login.terms": "Điều khoản",
    "login.and": "và",
    "login.privacy": "Chính sách quyền riêng tư",

    // Landing
    "landing.signIn": "Đăng nhập",
    "landing.pricing": "Bảng giá",
    "landing.features": "Tính năng",
    "landing.badge": "Vận hành bởi GPT-4o Vision",
    "landing.heroTitle1": "Trợ lý AI quản lý",
    "landing.heroTitle2": "hóa đơn",
    "landing.heroTitle3": "và gói đăng ký",
    "landing.heroSubtitle": "Quét hóa đơn, tự động phân loại chi tiêu và không bao giờ bỏ lỡ kỳ gia hạn. SubZero biến mớ hỗn độn tài chính thành sự rõ ràng.",
    "landing.continueGoogle": "Tiếp tục với Google",
    "landing.viewPricing": "Xem bảng giá",
    "landing.statPerScan": "Mỗi lần quét",
    "landing.statCategories": "Danh mục",
    "landing.statLanguages": "Ngôn ngữ",
    "landing.thisMonth": "Tháng này",
    "landing.featuresTitle": "Mọi thứ bạn cần để kiểm soát chi tiêu",
    "landing.featuresSubtitle": "Bộ công cụ tập trung cho hóa đơn, gói đăng ký và kỳ gia hạn.",
    "landing.feat1Title": "Trình quét hóa đơn AI",
    "landing.feat1Desc": "Chụp một tấm ảnh, GPT-4o Vision sẽ trích xuất cửa hàng, số tiền, ngày và danh mục chỉ trong vài giây.",
    "landing.feat2Title": "Theo dõi gói đăng ký",
    "landing.feat2Desc": "Gom mọi khoản thanh toán định kỳ về một nơi, kèm chu kỳ gia hạn và tổng chi phí.",
    "landing.feat3Title": "Nhắc gia hạn",
    "landing.feat3Desc": "Không còn bị trừ tiền bất ngờ. Xem những gì sẽ gia hạn trong 7 và 30 ngày tới.",
    "landing.feat4Title": "Phân tích chi tiêu",
    "landing.feat4Desc": "Xu hướng theo tháng và phân bổ theo danh mục trên bảng điều khiển tối, gọn gàng.",
    "landing.feat5Title": "Xuất CSV",
    "landing.feat5Desc": "Xuất toàn bộ chi tiêu ra CSV để phục vụ kế toán và thuế (Premium).",
    "landing.feat6Title": "Riêng tư & bảo mật",
    "landing.feat6Desc": "Dữ liệu của bạn được tách biệt theo từng tài khoản. Đăng nhập an toàn bằng Google.",
    "landing.pricingTitle": "Bảng giá đơn giản",
    "landing.pricingSubtitle": "Bắt đầu miễn phí. Nâng cấp khi bạn cần nhiều hơn.",
    "landing.free": "Miễn phí",
    "landing.freeF1": "Tối đa 10 lượt quét hóa đơn",
    "landing.freeF2": "Theo dõi tối đa 5 gói đăng ký",
    "landing.freeF3": "Bảng điều khiển & nhắc gia hạn",
    "landing.freeF4": "Phân loại bằng AI",
    "landing.getStartedFree": "Bắt đầu miễn phí",
    "landing.bestValue": "Đáng giá nhất",
    "landing.premium": "Premium",
    "landing.premF1": "Quét hóa đơn không giới hạn",
    "landing.premF2": "Gói đăng ký không giới hạn",
    "landing.premF3": "Xuất CSV cho kế toán",
    "landing.premF4": "Nhắc gia hạn ưu tiên",
    "landing.premF5": "Phân tích chi tiêu đầy đủ",
    "landing.upgradePremium": "Nâng cấp lên Premium",
    "landing.faqTitle": "Câu hỏi thường gặp",
    "landing.faq1Q": "Trình quét hóa đơn AI hoạt động như thế nào?",
    "landing.faq1A": "Tải lên hoặc chụp ảnh hóa đơn, công cụ GPT-4o Vision của chúng tôi sẽ tự động đọc cửa hàng, tổng tiền, ngày và danh mục. Bạn có thể xem lại và chỉnh sửa trước khi lưu.",
    "landing.faq2Q": "Gói Miễn phí và Premium khác nhau thế nào?",
    "landing.faq2A": "Gói Miễn phí bao gồm tối đa 10 lượt quét hóa đơn và 5 gói đăng ký được theo dõi. Premium mở khóa quét không giới hạn, gói đăng ký không giới hạn và xuất CSV.",
    "landing.faq3Q": "Làm sao để nâng cấp lên Premium?",
    "landing.faq3A": "Premium cho phiên bản Google Play sẽ sớm ra mắt. Với gói miễn phí, bạn đã có thể quản lý hóa đơn và gói đăng ký ngay bây giờ.",
    "landing.faq4Q": "Dữ liệu tài chính của tôi có an toàn không?",
    "landing.faq4A": "Có. Dữ liệu của mỗi tài khoản được tách biệt và bạn xác thực an toàn bằng tài khoản Google. Xem Chính sách quyền riêng tư và Thông báo xử lý bằng AI để biết chi tiết.",
    "landing.demoMerchant1": "Siêu thị",
    "landing.demoMerchant2": "Netflix",
    "landing.demoMerchant3": "Uber",

    // Dashboard
    "dashboard.welcome": "Chào mừng trở lại, {name}",
    "dashboard.subtitle": "Đây là tổng quan chi tiêu của bạn.",
    "dashboard.scanReceipt": "Quét hóa đơn",
    "kpi.thisMonth": "Tháng này",
    "kpi.receipts": "Hóa đơn",
    "kpi.subscriptions": "Gói đăng ký",
    "kpi.renewals": "Gia hạn (30 ngày)",
    "dashboard.monthlySpend": "Chi tiêu theo tháng",
    "dashboard.byCategory": "Theo danh mục",
    "dashboard.upcomingRenewals": "Sắp gia hạn",
    "dashboard.manage": "Quản lý",
    "dashboard.noExpensesMonth": "Tháng này chưa có khoản chi nào.",
    "dashboard.noRenewals": "Không có kỳ gia hạn nào trong 30 ngày tới.",
    "dashboard.addSubscription": "Thêm gói đăng ký",
    "dashboard.scanFirst": "Quét hóa đơn đầu tiên để xem xu hướng chi tiêu.",
    "dashboard.scanA": "Quét một hóa đơn",
    "chart.spend": "Chi tiêu",

    // Usage meter
    "usage.planUsage": "Mức sử dụng gói",
    "usage.receiptScans": "Lượt quét hóa đơn",
    "usage.subscriptions": "Gói đăng ký",
    "usage.unlimited": "Không giới hạn",
    "usage.upgradeUnlimited": "Nâng cấp để không giới hạn",

    // Renewal phrasing
    "renewal.overdue": "Quá hạn {n} ngày",
    "renewal.today": "Hôm nay",
    "renewal.inDays": "còn {n} ngày",
    "renewal.renewsToday": "Gia hạn hôm nay",
    "renewal.renewsIn": "Gia hạn sau {n} ngày",
    "renewal.noDate": "Chưa có ngày",
    "renewal.next": "Kỳ tới: {date}",

    // Scanner
    "scan.title": "Quét hóa đơn",
    "scan.subtitle": "Tải ảnh lên, AI sẽ tự động trích xuất thông tin.",
    "scan.dropHere": "Thả hóa đơn vào đây",
    "scan.orBrowse": "hoặc nhấn để chọn · PNG, JPG, WEBP",
    "scan.reading": "Đang đọc hóa đơn của bạn...",
    "scan.readingDesc": "GPT-4o Vision đang trích xuất cửa hàng, số tiền, ngày và danh mục.",
    "scan.receiptImage": "Ảnh hóa đơn",
    "scan.confidence": "Độ tin cậy AI {n}%",
    "scan.reviewSave": "Kiểm tra & lưu",
    "scan.merchantPlaceholder": "Tên cửa hàng",
    "scan.optional": "Không bắt buộc",
    "scan.saveReceipt": "Lưu hóa đơn",
    "scan.analyzing": "Đang phân tích hóa đơn...",
    "scan.chooserTitle": "Bạn muốn thêm hóa đơn bằng cách nào?",
    "scan.chooserHelp": "Chụp ảnh ngay hoặc chọn một ảnh có sẵn.",
    "scan.optCamera": "Chụp ảnh hóa đơn",
    "scan.optCameraHint": "Mở camera ngay trong SubZero. Ảnh được đưa thẳng vào quét và không lưu vào thư viện của bạn.",
    "scan.optCameraHintNative": "Mở ứng dụng camera của thiết bị. Ảnh có được lưu vào thư viện hay không tùy thuộc vào thiết bị.",
    "scan.cam.title": "Chụp ảnh hóa đơn",
    "scan.cam.hint": "Căn hóa đơn vào trong khung rồi nhấn nút chụp.",
    "scan.cam.starting": "Đang khởi động camera...",
    "scan.cam.shutter": "Chụp ảnh",
    "scan.cam.flip": "Đổi camera",
    "scan.cam.useNative": "Dùng camera của thiết bị",
    "scan.cam.errPermission": "Quyền truy cập camera đã bị từ chối. Hãy cho phép SubZero dùng camera hoặc dùng camera của thiết bị.",
    "scan.cam.errNoCamera": "Không tìm thấy camera trên thiết bị này.",
    "scan.cam.errGeneric": "Không thể khởi động camera trong ứng dụng.",
    "scan.cam.notSaved": "Ảnh chụp tại đây chỉ dùng để quét và không được lưu vào thư viện của bạn.",
    "scan.optGallery": "Chọn ảnh từ thư viện",
    "scan.optGalleryHint": "Chọn một ảnh có sẵn trên điện thoại.",
    "scan.optFile": "Chọn tệp",
    "scan.optFileHint": "PNG, JPG hoặc WEBP từ tệp của bạn.",
    "scan.dragHint": "Bạn cũng có thể kéo thả ảnh vào bất kỳ đâu trong khung này.",
    "scan.cancel": "Hủy",
    "warn.title": "Vui lòng kiểm tra trước khi lưu",
    "warn.amountHigh": "Lưu ý: số tiền nhận diện được có vẻ cao bất thường. Hãy kiểm tra lại trước khi lưu.",
    "warn.merchantMissing": "Không nhận diện được tên cửa hàng. Hãy điền vào trước khi lưu.",
    "warn.dateMissing": "Không nhận diện được ngày mua. Hãy kiểm tra lại trước khi lưu.",
    "warn.merchantAndDateMissing": "Không nhận diện được tên cửa hàng và ngày mua.",
    "warn.dateOld": "Hóa đơn này có ngày đã cũ \u2014 vẫn sẽ được lưu nhưng không tính vào tổng của tháng này.",
    "warn.dateConfirm": "Vui lòng xác nhận ngày ghi trên hóa đơn \u2014 trình quét chưa chắc chắn đã đọc đúng.",
    "warn.duplicate": "Hóa đơn này có thể đã có trong tài khoản của bạn. Hãy kiểm tra trước khi lưu lại.",
    "verify.title": "Kiểm tra chứng từ",
    "verify.green": "Không phát hiện mâu thuẫn rõ ràng",
    "verify.yellow": "Cần xem lại",
    "verify.red": "Một số dữ liệu có thể cần chỉnh sửa \u2014 hãy xem lại trước khi lưu",
    "verify.disclaimer": "Beta \u2014 tính năng thử nghiệm. Kết quả chỉ mang tính tham khảo và không xác nhận tính xác thực của chứng từ.",
    "verify.check.merchant": "Đã trích xuất cửa hàng",
    "verify.check.amount": "Đã trích xuất tổng tiền",
    "verify.check.date": "Đã trích xuất ngày",
    "verify.check.future": "Ngày không nằm trong tương lai",
    "verify.check.old": "Ngày nằm trong khoảng chấp nhận được",
    "verify.check.oldWarn": "Vui lòng kiểm tra ngày của chứng từ",
    "verify.check.confidence": "Ngày được đọc với độ tin cậy cao",
    "verify.check.consistency": "Các trường trên hóa đơn nhất quán với nhau",
    "warn.dateFuture": "Ngày mua nằm trong tương lai. Hãy kiểm tra lại trước khi lưu.",

    // Shared field labels
    "field.merchant": "Cửa hàng",
    "field.amount": "Số tiền",
    "field.currency": "Tiền tệ",
    "field.date": "Ngày",
    "field.category": "Danh mục",
    "field.notes": "Ghi chú",
    "field.name": "Tên",
    "field.billingCycle": "Chu kỳ thanh toán",
    "field.nextRenewal": "Ngày gia hạn tiếp theo",

    // Receipts
    "receipts.title": "Hóa đơn",
    "receipts.summary": "{count} hóa đơn · hiển thị {total}",
    "receipts.exportCsv": "Xuất CSV",
    "receipts.scan": "Quét",
    "receipts.searchPlaceholder": "Tìm cửa hàng...",
    "receipts.allCategories": "Tất cả danh mục",
    "receipts.noneTitle": "Chưa có hóa đơn",
    "receipts.noneDesc": "Quét hóa đơn đầu tiên để bắt đầu.",
    "receipts.noMatch": "Không có kết quả",
    "receipts.noMatchDesc": "Thử từ khóa hoặc bộ lọc khác.",
    "receipts.scanAreceipt": "Quét một hóa đơn",
    "receipts.colMerchant": "Cửa hàng",
    "receipts.colCategory": "Danh mục",
    "receipts.colDate": "Ngày",
    "receipts.colAmount": "Số tiền",
    "receipts.oldNote": "Hóa đơn cũ vẫn được lưu tại đây, nhưng chỉ hóa đơn của tháng hiện tại mới được tính vào \u201cTháng này\u201d.",
    "receipts.notThisMonth": "không thuộc tháng này",
    "receipts.deleteTitle": "Xóa hóa đơn?",
    "receipts.deleteDesc": "Thao tác này sẽ xóa vĩnh viễn \"{merchant}\" ({amount}).",

    // Subscriptions
    "subs.title": "Gói đăng ký",
    "subs.summary": "{count} đang hoạt động · ~{total}/tháng định kỳ",
    "subs.add": "Thêm gói đăng ký",
    "subs.noneTitle": "Chưa có gói đăng ký",
    "subs.noneDesc": "Thêm các khoản thanh toán định kỳ để theo dõi kỳ gia hạn.",
    "subs.namePlaceholder": "VD: Netflix",
    "subs.editTitle": "Sửa gói đăng ký",
    "subs.addTitle": "Thêm gói đăng ký",
    "subs.deleteTitle": "Xóa gói đăng ký?",
    "subs.deleteDesc": "Ngừng theo dõi \"{name}\"?",

    // Settings
    "settings.title": "Cài đặt",
    "settings.account": "Tài khoản",
    "settings.goPremium": "Lên Premium",
    "settings.goPremiumDesc": "Quét và gói đăng ký không giới hạn, kèm xuất CSV.",
    "settings.upgradeNow": "Nâng cấp ngay",
    "settings.legal": "Pháp lý",
    "settings.privacy": "Chính sách quyền riêng tư",
    "settings.terms": "Điều khoản dịch vụ",
    "settings.aiNotice": "Thông báo xử lý bằng AI",
    "settings.deleteAccount": "Xóa tài khoản / Yêu cầu xóa dữ liệu",
    "settings.language": "Ngôn ngữ",

    // Admin
    "admin.title": "Bảng quản trị",
    "admin.subtitle": "Kích hoạt Premium thủ công cho người dùng.",
    "admin.activateByEmail": "Kích hoạt Premium theo email",
    "admin.emailPlaceholder": "user@example.com",
    "admin.activatePremium": "Kích hoạt Premium",
    "admin.activating": "Đang kích hoạt...",
    "admin.mustSignIn": "Người dùng phải đăng nhập ít nhất một lần trước khi kích hoạt.",
    "admin.searchUsers": "Tìm người dùng...",
    "admin.joined": "tham gia",
    "admin.noUsers": "Không tìm thấy người dùng.",
    "admin.adminTag": "Quản trị viên",

    // Upgrade modal
    "upgrade.title": "Nâng cấp lên Premium",
    "upgrade.premium": "Premium",
    "upgrade.oneTime": "\u2014 sắp có trên Google Play",
    "upgrade.f1": "Quét hóa đơn không giới hạn",
    "upgrade.f2": "Theo dõi gói đăng ký không giới hạn",
    "upgrade.f3": "Xuất CSV toàn bộ chi tiêu",
    "upgrade.f4": "Nhắc gia hạn ưu tiên",
    "upgrade.f5": "Phân tích chi tiêu đầy đủ",
    "upgrade.continue": "Tiếp tục thanh toán",
    "upgrade.afterPayment": "Sau khi thanh toán, đội ngũ của chúng tôi sẽ kích hoạt Premium cho tài khoản của bạn. Bạn sẽ nhận được thông báo qua email.",
    "upgrade.appComingSoon": "Premium trên phiên bản Google Play sẽ sớm ra mắt.",
    "upgrade.appComingSoonHint": "Bạn đã có thể quản lý hóa đơn và gói đăng ký với gói miễn phí.",

    // Google Play Billing (chỉ trong ứng dụng Android)
    "billing.monthly": "Hằng tháng",
    "billing.yearly": "Hằng năm",
    "billing.perMonth": "thanh toán hằng tháng, hủy bất cứ lúc nào",
    "billing.perYear": "thanh toán hằng năm, hủy bất cứ lúc nào",
    "billing.buy": "Đăng ký",
    "billing.restore": "Khôi phục giao dịch",
    "billing.playNotice": "Thanh toán được Google Play xử lý an toàn. Premium chỉ được mở khóa sau khi SubZero xác minh giao dịch.",
    "billing.success": "Đã kích hoạt Premium. Cảm ơn bạn!",
    "billing.restored": "Gói Premium của bạn đã được khôi phục.",
    "billing.cancelled": "Đã hủy giao dịch.",
    "billing.alreadyPremium": "Tài khoản này đã có Premium.",
    "billing.catalogError": "Không thể tải giá từ Google Play.",
    "billing.retry": "Thử lại",
    "billing.err.unavailable": "Google Play Billing không khả dụng tại đây. Hãy dùng ứng dụng SubZero từ Google Play.",
    "billing.err.purchaseFailed": "Không thể bắt đầu giao dịch. Vui lòng thử lại.",
    "billing.err.verifyFailed": "Không thể xác minh giao dịch. Premium chưa được kích hoạt. Hãy thử \"Khôi phục giao dịch\" sau ít phút.",
    "billing.err.verifyUnavailable": "Xác minh giao dịch tạm thời không khả dụng. Giao dịch của bạn vẫn an toàn - hãy thử \"Khôi phục giao dịch\" sau.",
    "billing.err.verifyRejected": "Google Play không xác nhận giao dịch này cho tài khoản của bạn.",
    "billing.err.tokenOtherAccount": "Giao dịch này đã được liên kết với một tài khoản SubZero khác.",
    "billing.err.notActive": "Gói đăng ký này không còn hoạt động.",
    "billing.err.googleUnavailable": "Google Play tạm thời không khả dụng. Vui lòng thử lại.",
    "billing.err.notSignedIn": "Vui lòng đăng nhập lại để tiếp tục.",
    "billing.err.nothingToRestore": "Không tìm thấy giao dịch nào cho tài khoản Google này.",
    "billing.err.nothingActive": "Không tìm thấy gói Premium nào đang hoạt động.",

    // Toasts
    "toast.scanned": "Đã quét hóa đơn! Kiểm tra thông tin bên dưới.",
    "toast.scanFailed": "Quét thất bại. Vui lòng thử lại.",
    "toast.imageType": "Vui lòng tải lên ảnh PNG, JPG hoặc WEBP.",
    "toast.merchantRequired": "Cần nhập tên cửa hàng.",
    "toast.receiptSaved": "Đã lưu hóa đơn.",
    "toast.receiptSaveError": "Không thể lưu hóa đơn.",
    "toast.receiptDeleted": "Đã xóa hóa đơn.",
    "toast.deleteError": "Không thể xóa.",
    "toast.csvPremium": "Xuất CSV là tính năng Premium.",
    "toast.csvExported": "Đã xuất CSV.",
    "toast.exportFailed": "Xuất thất bại.",
    "toast.nameRequired": "Cần nhập tên.",
    "toast.subUpdated": "Đã cập nhật gói đăng ký.",
    "toast.subAdded": "Đã thêm gói đăng ký.",
    "toast.subDeleted": "Đã xóa.",
    "toast.saveError": "Không thể lưu.",
    "toast.enterEmail": "Nhập địa chỉ email.",
    "toast.premiumActivated": "Đã kích hoạt Premium cho {email}",
    "toast.activateError": "Không thể kích hoạt.",
    "toast.updateFailed": "Cập nhật thất bại.",
    "toast.usersLoadError": "Không thể tải danh sách người dùng.",

    // Auth callback / legal chrome
    "auth.signingIn": "Đang đăng nhập...",
    "legal.deleteAccountTab": "Xóa tài khoản",

    // Frontend-side localization of backend error messages
    "err.freeLimitScans": "Đã đạt giới hạn gói miễn phí ({n} hóa đơn). Nâng cấp lên Premium để quét không giới hạn.",
    "err.freeLimitSubs": "Đã đạt giới hạn gói miễn phí ({n} gói đăng ký). Nâng cấp lên Premium để theo dõi không giới hạn.",
    "err.emptyFile": "Tệp trống",
    "err.imageTooLarge": "Ảnh quá lớn (tối đa {n} MB)",
    "err.couldNotRead": "Không thể đọc hóa đơn",
    "err.receiptNotFound": "Không tìm thấy hóa đơn",
    "err.subNotFound": "Không tìm thấy gói đăng ký",
  },
};

// Category display labels (DB values stay English; only display is translated)
export const CATEGORY_LABELS = {
  en: {
    Groceries: "Groceries", Dining: "Dining", Transport: "Transport", Travel: "Travel",
    Shopping: "Shopping", Bills: "Bills", Healthcare: "Healthcare",
    Entertainment: "Entertainment", Software: "Software", Other: "Other",
  },
  pl: {
    Groceries: "Spo\u017cywcze", Dining: "Restauracje", Transport: "Transport", Travel: "Podr\u00f3\u017ce",
    Shopping: "Zakupy", Bills: "Rachunki", Healthcare: "Zdrowie",
    Entertainment: "Rozrywka", Software: "Oprogramowanie", Other: "Inne",
  },
  vi: {
    Groceries: "Thực phẩm", Dining: "Ăn uống", Transport: "Di chuyển", Travel: "Du lịch",
    Shopping: "Mua sắm", Bills: "Hóa đơn dịch vụ", Healthcare: "Sức khỏe",
    Entertainment: "Giải trí", Software: "Phần mềm", Other: "Khác",
  },
};

// Billing cycle display labels (DB values stay English)
export const CYCLE_LABELS = {
  en: { monthly: "monthly", yearly: "yearly", weekly: "weekly", quarterly: "quarterly" },
  pl: { monthly: "miesi\u0119cznie", yearly: "rocznie", weekly: "tygodniowo", quarterly: "kwartalnie" },
  vi: { monthly: "hằng tháng", yearly: "hằng năm", weekly: "hằng tuần", quarterly: "hằng quý" },
};
